import { FC, useState } from 'react';
import ReactTooltip from 'react-tooltip';
import { Popover } from '@headlessui/react';

import { ReactComponent as FilterListIcon } from 'assets/icons/filter_list.svg';
import { ReactComponent as CollapseIcon } from 'assets/icons/chevron-left.svg';
import { ReactComponent as AddCircleIcon } from 'assets/icons/add_circle_outline.svg';
import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import { PRACTITIONER_FILTER_OPTIONS } from 'utils/constants';
import CreateOrEditService, {
  CreateOrEditServiceSubmitData,
} from 'pages/ServicesPage/Table/CreateOrEditService/CreateOrEditService';
import useClinic from 'hooks/useClinic';

import GeneralSettingModal from '../GeneralSettingModal/GeneralSettingModal';
import axiosInstance from 'apis/axiosInstance';
import { convertDurationForBE } from 'utils/convertDuration';
import { loadSelectedClinicId } from 'utils/storage';
import useMatchMutate from 'hooks/useMatchMutate';
import { renderToast } from 'components/Toast/Toast';
import { BAD_REQUEST } from 'utils/constants/statusCode';
import CommonButton from 'components/CommonButton/CommonButton';

interface SidePanelProps {
  options: { [key: string]: string };
  isCollapsed: boolean;
  onCollapsedIconClicked: () => void;
  selectedFilter: string;
  onFilterOptionClicked: (option: string) => void;
  isOptionDropdownDisabled: (option: string) => boolean;
  numberOfFilters: number;
  isOrdering: boolean;
  onOrdering: () => void;
  onDoneOrdering: () => void;
}

const { ALL } = PRACTITIONER_FILTER_OPTIONS;

const THREE_DOTS_OPTIONS = [
  {
    label: 'Sort Services',
    name: 'sortServices',
  },
  {
    label: 'General Service Settings',
    name: 'generalServiceSettings',
  },
];

const SidePanel: FC<SidePanelProps> = ({
  isCollapsed,
  isOptionDropdownDisabled,
  onCollapsedIconClicked,
  selectedFilter,
  onFilterOptionClicked,
  numberOfFilters,
  children,
  options,
  isOrdering,
  onOrdering,
  onDoneOrdering,
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isGeneralSettingOpen, setIsGeneralSettingOpen] = useState(false);

  const matchMutate = useMatchMutate();

  const { data: clinic } = useClinic();

  const onOptionClick = (option: { label: string; name: string }) => {
    const generalSetting = option === THREE_DOTS_OPTIONS[1];

    if (!generalSetting) {
      onOrdering();
      return;
    }

    setIsGeneralSettingOpen(true);
  };

  const onCreateService = async (data: CreateOrEditServiceSubmitData) => {
    const id = loadSelectedClinicId();
    try {
      await axiosInstance.post(`clinics/${id}/services`, {
        ...data,
        duration: convertDurationForBE(data.duration!),
        isActive: data.status === 'active',
      });
      await matchMutate(/\/services\?[\s\S]+/);
      renderToast({
        type: 'success',
        message: 'Added a service successfully',
      });
      setIsModalOpen(false);
    } catch (error: any) {
      if (error.response?.status === BAD_REQUEST) {
        return renderToast({
          message: error.response?.data.message,
          type: 'error',
        });
      }

      renderToast({
        type: 'error',
        message: 'Something went wrong. Please try again later',
      });
    }
  };

  return (
    <>
      {isModalOpen && clinic && (
        <CreateOrEditService
          isCreateMode
          onClose={() => setIsModalOpen(false)}
          onSubmit={onCreateService}
          timeInterval={clinic.timeInterval}
        />
      )}
      {isGeneralSettingOpen && (
        <GeneralSettingModal
          key={`${isGeneralSettingOpen}`}
          clinic={clinic}
          onClose={() => setIsGeneralSettingOpen(false)}
        />
      )}
      <div
        className={`transition-[width] duration-300 ${
          isCollapsed ? 'w-[10rem]' : 'w-[34rem]'
        } z-20`}
      >
        <div className="sticky top-[6rem] h-[calc(100vh-6rem)] border-r border-light-grey text-darkest-grey text-14 leading-21">
          <div className="flex h-8.4 items-center border-b border-light-grey relative">
            <button
              data-for={'panel-expand-collapse-button'}
              data-tip=""
              className="w-2.4 h-2.4 bg-white rounded-full flex items-center justify-center shadow-primary absolute -right-1 top-0.8 disabled:cursor-not-allowed disabled:hidden"
              disabled={isOrdering}
              onClick={onCollapsedIconClicked}
            >
              <CollapseIcon
                className={`w-1.4 h-1.4 transition-transform duration-300 ${
                  isCollapsed && 'rotate-180'
                }`}
              />
            </button>

            <ReactTooltip
              id={'panel-expand-collapse-button'}
              place="right"
              effect="solid"
              className="!border-light-grey !rounded-[0.8rem] !h-2.6 !p-0.8 flex justify-center items-center z-[100] !bg-white shadow-tooltip"
            >
              <span className="text-11 text-darkest-grey flex h-full items-center">
                {isCollapsed ? 'Expand' : 'Collapse'}
              </span>
            </ReactTooltip>

            <span
              className={`flex w-full leading-[1.8rem] text-11 absolute top-5.5 ${
                isCollapsed ? 'justify-center left-0' : 'left-3.9'
              }`}
            >
              {selectedFilter} ({numberOfFilters})
            </span>

            <div className="flex gap-x-1 items-center pl-3.9">
              <h3
                className={`leading-[3.6rem] text-24 font-bold  ${
                  isCollapsed ? 'hidden' : 'block'
                }`}
              >
                Services
              </h3>
              <Popover className="relative">
                {({ open }) => {
                  const isFilterNotDefault = selectedFilter !== ALL;
                  return (
                    <>
                      <Popover.Button
                        aria-label="Filter services"
                        className="flex items-center group"
                      >
                        <FilterListIcon
                          className={`w-2.4 h-2.4 group-hover:all-child:all-child:fill-magenta ${
                            open || isFilterNotDefault
                              ? 'all-child:all-child:fill-magenta'
                              : ''
                          }`}
                        />
                      </Popover.Button>
                      <Popover.Panel className="w-11.2 flex flex-col gap-y-1.5 mt-0.6 py-1.6 absolute -left-4 z-10 bg-white shadow-primary rounded-[1rem] font-normal">
                        {Object.values(options).map((option) => (
                          <Popover.Button
                            key={option}
                            className={`w-full pl-2 text-left hover:text-magenta ${
                              selectedFilter === option
                                ? 'text-magenta'
                                : 'text-darkest-grey'
                            } disabled:text-grey`}
                            onClick={() => onFilterOptionClicked(option)}
                            disabled={isOptionDropdownDisabled(option)}
                          >
                            {option}
                          </Popover.Button>
                        ))}
                      </Popover.Panel>
                    </>
                  );
                }}
              </Popover>
              {isOrdering ? (
                <CommonButton
                  className="!min-h-[2.6rem] !min-w-[9.6rem] !text-11 !leading-[1.8rem] ml-3.9"
                  onClick={onDoneOrdering}
                >
                  Done Sorting
                </CommonButton>
              ) : (
                <div className="ml-7 flex items-center gap-x-1.2">
                  <div>
                    <button
                      aria-label="Create new service"
                      className={`flex items-center gap-x-1 text-magenta transition-all ${
                        isCollapsed ? 'hidden' : 'block'
                      }`}
                      data-for={'add-service-button'}
                      data-tip=""
                      onClick={() => setIsModalOpen(!isModalOpen)}
                    >
                      <AddCircleIcon className="w-2.4 h-2.4 all-child:all-child:fill-magenta" />
                    </button>
                    <ReactTooltip
                      id={'add-service-button'}
                      place="bottom"
                      effect="solid"
                      className="!border-light-grey !rounded-[0.8rem] !h-2.6 !p-0.8 flex justify-center items-center z-[100] !bg-white shadow-tooltip"
                    >
                      <span className="text-11 text-darkest-grey flex h-full items-center">
                        Create new service
                      </span>
                    </ReactTooltip>
                  </div>
                  <div>
                    <Popover className={isCollapsed ? 'hidden' : 'relative'}>
                      {({ open }) => {
                        const isFilterNotDefault = selectedFilter !== ALL;
                        return (
                          <>
                            <Popover.Button
                              aria-label="Service list options"
                              className="flex items-center group"
                            >
                              <MoreIcon
                                className={`w-2.4 h-2.4 group-hover:all-child:all-child:fill-magenta ${
                                  open || isFilterNotDefault
                                    ? 'all-child:all-child:fill-magenta'
                                    : ''
                                }`}
                              />
                            </Popover.Button>
                            <Popover.Panel className="w-19.8 flex flex-col gap-y-1.5 mt-0.6 py-1.6 absolute -left-4 z-10 bg-white shadow-primary rounded-[1rem] font-normal">
                              {THREE_DOTS_OPTIONS.map((option) => (
                                <Popover.Button
                                  key={option.label}
                                  className={`w-full pl-2 text-left hover:text-magenta`}
                                  onClick={() => onOptionClick(option)}
                                >
                                  {option.label}
                                </Popover.Button>
                              ))}
                            </Popover.Panel>
                          </>
                        );
                      }}
                    </Popover>
                  </div>
                </div>
              )}
            </div>
          </div>
          <div
            className="overflow-y-auto h-[calc(100%-8.4rem)] scrollbar"
            key={selectedFilter}
          >
            {children}
          </div>
        </div>
      </div>
    </>
  );
};

export default SidePanel;
